import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import ThemePicker from './ThemePicker';
import ChangePassword from './dashboards/ChangePassword';  // ✅ NEW IMPORT
import DailyInventory from './dashboards/DailyInventory';
import RepackOutDashboard from './dashboards/RepackOutDashboard';
import ShipmentsPage from './dashboards/ShipmentsPage';
import QCSamplesPage from './dashboards/QCSamplesPage';
import ShipmentPlanningDashboard from './dashboards/ShipmentPlanningDashboard'; // ✅ NEW
import ConsolidatedSummary from './dashboards/ConsolidatedSummary';
import DailySummary from './dashboards/DailySummary';
import AnalyticsDashboard from './dashboards/AnalyticsDashboard';
import RepackOutAnalytics from './dashboards/RepackOutAnalytics';
import ApprovalRequests from './dashboards/ApprovalRequests';
import PasswordResetRequests from './dashboards/PasswordResetRequests';
import UserManagement from './dashboards/UserManagement';
// RoleManagement is now integrated into UserManagement (Roles tab)
import ChangeLogDashboard from './dashboards/ChangeLogDashboard';
import NAVValidationDashboard from './dashboards/NAVValidationDashboard';
import DPRValidationDashboard from './dashboards/DPRValidationDashboard';
import AuditTrailDashboard from './dashboards/AuditTrailDashboard';
import EmailNotificationSettings from './dashboards/EmailNotificationSettings'; // ✅ Phase 1b: Email Module
import StockReport from './StockReport';  // ✅ Stock Inward/Outward Report
import ExecutiveDashboard from './dashboards/ExecutiveDashboard';  // ✅ NEW: Executive Dashboard
import MasterManagement from './dashboards/MasterManagement';     // ✅ Master Management

// ✅ Cold Store Management pages
import ColdStoreInPage from '../pages/ColdStoreInPage';
import ColdStoreOutPage from '../pages/ColdStoreOutPage';

// ✅ Operations Dashboard + Reports
import OperationsContainer from '../pages/OperationsContainer';
import RecallDestuff from '../pages/RecallDestuff';  // ✅ Recall & De-stuff workflow
import LotTraceabilityReport from '../pages/LotTraceabilityReport';
import PalletAgingReport from '../pages/PalletAgingReport';
import FCLManifestReport from '../pages/FCLManifestReport';

import '../styles/Dashboard.css';

// ===== CONFIGURATION =====
const allTabs = [
  // ✅ NEW: Executive Dashboard - appears first for executives
  { id: 'executive_dashboard', label: 'Executive Dashboard', component: ExecutiveDashboard, category: 'executive', icon: '⭐', color: '#1e3a5f' },

  // ✅ Cold Store Management
  { id: 'cold_store_in',  label: 'Cold Store IN',  component: ColdStoreInPage,  category: 'cold_store', icon: '❄️', color: '#1F618D' },
  { id: 'cold_store_out', label: 'Cold Store OUT', component: ColdStoreOutPage, category: 'cold_store', icon: '📦', color: '#C0392B' },
  { id: 'cs_operations_dashboard', label: 'Operations Dashboard', component: OperationsContainer, category: 'cold_store', icon: '📊', color: '#8E44AD' },
  { id: 'cs_recall_destuff', label: 'Recall & De-stuff', component: RecallDestuff, category: 'cold_store', icon: '♻️', color: '#7c3aed' },

  { id: 'daily_inventory', label: 'Daily Inventory', component: DailyInventory, category: 'inventory', icon: '📊', color: '#3b82f6' },
  { id: 'repack_out', label: 'Repack-Out', component: RepackOutDashboard, category: 'inventory', icon: '📦', color: '#8b5cf6' },
  { id: 'shipments', label: 'Shipments', component: ShipmentsPage, category: 'inventory', icon: '🚚', color: '#10b981' },
  { id: 'qc_samples', label: 'QC Samples', component: QCSamplesPage, category: 'inventory', icon: '🔬', color: '#f59e0b' },
  { id: 'shipment_planning', label: 'Shipment Planning', component: ShipmentPlanningDashboard, category: 'inventory', icon: '📋', color: '#6366f1' }, // ✅ NEW
  { id: 'consolidated_summary', label: 'Consolidated Summary', component: ConsolidatedSummary, category: 'summaries', icon: '📈', color: '#06b6d4' },
  { id: 'daily_summary', label: 'Daily Summary', component: DailySummary, category: 'summaries', icon: '📋', color: '#14b8a6' },
  { id: 'analytics', label: 'Production Analytics', component: AnalyticsDashboard, category: 'analytics', icon: '📊', color: '#8b5cf6' },
  { id: 'repack_analytics', label: 'Repack-Out Analytics', component: RepackOutAnalytics, category: 'analytics', icon: '📦', color: '#a855f7' },
  { id: 'approval_requests', label: 'Approval Requests', component: ApprovalRequests, category: 'admin', icon: '✅', color: '#22c55e' },
  { id: 'password_resets', label: 'Password Resets', component: PasswordResetRequests, category: 'admin', icon: '🔑', color: '#ef4444' },
  { id: 'user_management', label: 'User Management', component: UserManagement, category: 'admin', icon: '👥', color: '#6366f1' },
  { id: 'change_log', label: 'Change Log', component: ChangeLogDashboard, category: 'admin', icon: '📝', color: '#ec4899' },
  { id: 'erp_validation', label: 'NAV Data Validation', component: NAVValidationDashboard, category: 'admin', icon: '📊', color: '#0ea5e9' },
  { id: 'dpr_validation', label: 'DPR Validation', component: DPRValidationDashboard, category: 'admin', icon: '📋', color: '#10b981' },
  { id: 'audit_trail', label: 'Audit Trail', component: AuditTrailDashboard, category: 'admin', icon: '🔍', color: '#f97316' },
  { id: 'email_notifications', label: 'Email Notifications', component: EmailNotificationSettings, category: 'admin', icon: '📧', color: '#0369a1' }, // ✅ Phase 1b
  // ✅ Master Reports
  { id: 'stock_report', label: 'Stock Inward/Outward', component: StockReport, category: 'reports', icon: '🔄', color: '#0891b2' },
  { id: 'fcl_manifest', label: 'FCL Manifest',     component: FCLManifestReport,    category: 'reports', icon: '📋', color: '#5b21b6' },
  { id: 'pallet_aging', label: 'Pallet Aging',     component: PalletAgingReport,    category: 'reports', icon: '⏱',  color: '#ea580c' },
  { id: 'lot_trace',    label: 'Lot Traceability', component: LotTraceabilityReport, category: 'reports', icon: '🔍', color: '#1F618D' },

  // ✅ Master Management
  { id: 'master_products',   label: 'Products',        component: MasterManagement, category: 'masters', icon: '🦐', color: '#0891b2' },
  { id: 'master_brands',     label: 'Brands',          component: MasterManagement, category: 'masters', icon: '🏷️', color: '#0891b2' },
  { id: 'master_grades',     label: 'Grades',          component: MasterManagement, category: 'masters', icon: '📏', color: '#0891b2' },
  { id: 'master_pack_sizes', label: 'Pack Sizes',      component: MasterManagement, category: 'masters', icon: '📦', color: '#0891b2' },
  { id: 'master_nav',        label: 'NAV ID Mappings', component: MasterManagement, category: 'masters', icon: '🔗', color: '#0891b2' },
];

// ✅ Maps sidebar tab id → MasterManagement internal tab index
const MASTER_TAB_MAP = {
  master_products:   0,
  master_brands:     1,
  master_grades:     2,
  master_pack_sizes: 3,
  master_nav:        4,
};

const menuCategories = [
  // ✅ NEW: Executive Dashboard category - appears at top
  { id: 'executive', label: 'Executive Dashboard', icon: '⭐', color: '#1e3a5f' },

  // ✅ Cold Store Management category
  { id: 'cold_store', label: 'Cold Store Management', icon: '🏪', color: '#8E44AD' },

  { id: 'inventory', label: 'Inventory Management', icon: '📦', color: '#3b82f6' },
  { id: 'summaries', label: 'Daily Summaries', icon: '📋', color: '#14b8a6' },
  { id: 'analytics', label: 'Analytics Dashboard', icon: '📊', color: '#8b5cf6' },
  { id: 'reports', label: 'Master Reports', icon: '📑', color: '#0891b2' },  // ✅ NEW
  { id: 'masters', label: 'Master Management', icon: '🗂️', color: '#0891b2' },
  { id: 'admin', label: 'Administration', icon: '⚙️', color: '#6366f1' }
];

// ===== AUTH HELPER =====
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    },
    withCredentials: true
  };
};

// ===== MEMOIZED COMPONENTS =====
const ToastNotification = memo(({ toast, onClose }) => {
  if (!toast.show) return null;
  
  return (
    <div className={`toast-notification toast-${toast.type}`}>
      <div className="toast-icon">
        {toast.type === 'success' ? '✅' : 
         toast.type === 'error' ? '❌' : 
         toast.type === 'warning' ? '⚠️' : 'ℹ️'}
      </div>
      <div className="toast-content">
        <span className="toast-message">{toast.message}</span>
      </div>
      <button className="toast-close" onClick={onClose}>✕</button>
    </div>
  );
});

const ConfirmModal = memo(({ modal, onClose, onConfirm }) => {
  if (!modal.show) return null;
  
  return (
    <>
      <div className="modal-overlay-modern" onClick={onClose}></div>
      <div className="confirmation-modal">
        <div className="modal-header-modern">
          <h3 className="modal-title-modern">{modal.title}</h3>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body-modern">
          <p className="modal-message">{modal.message}</p>
        </div>
        <div className="modal-footer-modern">
          <button className="modal-btn modal-btn-cancel" onClick={onClose}>
            <span>✕</span> Cancel
          </button>
          <button 
            className={`modal-btn modal-btn-confirm ${modal.type === 'danger' ? 'danger' : ''}`}
            onClick={onConfirm}
          >
            <span>✓</span> Confirm
          </button>
        </div>
      </div>
    </>
  );
});

// ===== NOTIFICATIONS DROPDOWN COMPONENT =====
const NotificationsDropdown = memo(({ 
  notifications, 
  unreadCount, 
  isOpen, 
  onToggle, 
  onNotificationClick, 
  onMarkAllRead,
  onClose 
}) => {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationConfig = (type) => {
    const configs = {
      'APPROVAL_REQUEST': { 
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
          </svg>
        ),
        color: '#6366f1',
        bgColor: 'rgba(99, 102, 241, 0.12)',
        label: 'Approval Request'
      },
      'APPROVAL_RESPONSE': { 
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
            <polyline points="22,4 12,14.01 9,11.01"/>
          </svg>
        ),
        color: '#10b981',
        bgColor: 'rgba(16, 185, 129, 0.12)',
        label: 'Approved'
      },
      'PASSWORD_RESET_REQUEST': { 
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0110 0v4"/>
          </svg>
        ),
        color: '#f59e0b',
        bgColor: 'rgba(245, 158, 11, 0.12)',
        label: 'Password Reset'
      },
      'SYSTEM': { 
        icon: (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        ),
        color: '#3b82f6',
        bgColor: 'rgba(59, 130, 246, 0.12)',
        label: 'System'
      }
    };
    return configs[type] || configs['SYSTEM'];
  };

  return (
    <div className="notifications-wrapper" ref={dropdownRef}>
      <button 
        className={`notification-btn ${unreadCount > 0 ? 'has-unread' : ''}`}
        onClick={onToggle}
        aria-label="Notifications"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 01-3.46 0"/>
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div className="notifications-dropdown">
          <div className="notifications-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button className="mark-read-btn" onClick={onMarkAllRead}>
                Mark all read
              </button>
            )}
          </div>
          
          <div className="notifications-list">
            {notifications.length === 0 ? (
              <div className="no-notifications">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 01-3.46 0"/>
                </svg>
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const config = getNotificationConfig(notification.type);
                return (
                  <div 
                    key={notification.id}
                    className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                    onClick={() => onNotificationClick(notification)}
                  >
                    <div 
                      className="notification-icon"
                      style={{ backgroundColor: config.bgColor, color: config.color }}
                    >
                      {config.icon}
                    </div>
                    <div className="notification-content">
                      <p className="notification-message">{notification.message}</p>
                      <span className="notification-time">{formatTime(notification.created_at)}</span>
                    </div>
                    {!notification.is_read && <span className="unread-dot"></span>}
                  </div>
                );
              })
            )}
          </div>

          {notifications.length > 0 && (
            <div className="notifications-footer">
              <button 
                className="view-all-btn"
                onClick={() => onNotificationClick({ reference_type: 'view_all' })}
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// ===== USER DROPDOWN COMPONENT =====
const UserDropdown = memo(({ user, onChangePassword, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getAvatarColor = (role) => {
    const colors = {
      'admin': 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'manager': 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'supervisor': 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'data_entry': 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'viewer': 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
    };
    return colors[role] || colors['viewer'];
  };

  return (
    <div className="user-dropdown-wrapper" ref={dropdownRef}>
      <button 
        className="user-profile-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="User menu"
      >
        <div 
          className="user-avatar-modern"
          style={{ background: getAvatarColor(user?.role) }}
        >
          {getInitials(user?.full_name)}
        </div>
        <div className="user-info-modern">
          <span className="user-name-modern">{user?.full_name || 'User'}</span>
          <span className="user-role-modern">{user?.role?.replace('_', ' ').toUpperCase() || 'USER'}</span>
        </div>
        <svg 
          className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
        >
          <polyline points="6,9 12,15 18,9"/>
        </svg>
      </button>

      {isOpen && (
        <div className="user-dropdown-menu">
          <div className="dropdown-header">
            <div 
              className="dropdown-avatar"
              style={{ background: getAvatarColor(user?.role) }}
            >
              {getInitials(user?.full_name)}
            </div>
            <div className="dropdown-user-info">
              <span className="dropdown-name">{user?.full_name}</span>
              <span className="dropdown-email">{user?.email}</span>
            </div>
          </div>
          
          <div className="dropdown-divider"></div>
          
          <button className="dropdown-item" onClick={() => { setIsOpen(false); onChangePassword(); }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
            Change Password
          </button>
          
          <div className="dropdown-divider"></div>
          
          <button className="dropdown-item logout" onClick={() => { setIsOpen(false); onLogout(); }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
              <polyline points="16,17 21,12 16,7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Logout
          </button>
        </div>
      )}
    </div>
  );
});

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('daily_inventory');
  const [activeCategory, setActiveCategory] = useState(null);
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [confirmModal, setConfirmModal] = useState({ 
    show: false, 
    title: '', 
    message: '', 
    onConfirm: null,
    type: 'info'
  });
  const [currentTheme, setCurrentTheme] = useState(
    () => localStorage.getItem('dashboard-theme') || 'coldstore'
  );
  
  // ✅ NOTIFICATIONS STATE
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationIntervalRef = useRef(null);

  // ✅ NEW: CHANGE PASSWORD STATE
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [forceChangePassword, setForceChangePassword] = useState(false);

  // ✅ NEW: Highlighted request ID from URL (for email links)
  const [highlightedRequestId, setHighlightedRequestId] = useState(null);

  // ✅ FCL Manifest deep-link from Operations Dashboard pending approvals
  const [prefilledFcl, setPrefilledFcl] = useState(null);

  // ✅ Handle URL parameters for tab and request_id (from email links)
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const requestIdParam = searchParams.get('request_id');
    
    if (tabParam) {
      const validTab = allTabs.find(t => t.id === tabParam);
      if (validTab) {
        setActiveTab(tabParam);
        setActiveCategory(validTab.category);
      }
    }
    
    if (requestIdParam) {
      setHighlightedRequestId(parseInt(requestIdParam, 10));
    }
  }, [location.search]);

  // ===== HANDLERS =====
  const showToast = useCallback((message, type = 'info', duration = 4000) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), duration);
  }, []);

  const showConfirmation = useCallback((title, message, onConfirm, type = 'warning') => {
    setConfirmModal({ show: true, title, message, onConfirm, type });
  }, []);

  const closeConfirmModal = useCallback(() => {
    setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'info' });
  }, []);

  const handleModalConfirm = useCallback(() => {
    if (confirmModal.onConfirm) {
      confirmModal.onConfirm();
    }
    closeConfirmModal();
  }, [confirmModal.onConfirm, closeConfirmModal]);

  const handleCategoryClick = useCallback((categoryId) => {
    setActiveCategory(categoryId);
    const firstTab = allTabs.find(t => t.category === categoryId && permissions[t.id] === true);
    if (firstTab) setActiveTab(firstTab.id);
  }, [permissions]);

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    const tab = allTabs.find(t => t.id === tabId);
    if (tab) setActiveCategory(tab.category);
    showToast(`📂 Switched to ${allTabs.find(t => t.id === tabId)?.label}`, 'info', 2000);
  }, [showToast]);

  const handleThemeChange = useCallback((themeId) => {
    setCurrentTheme(themeId);
    document.documentElement.setAttribute('data-theme', themeId);
    localStorage.setItem('dashboard-theme', themeId);
  }, []);

  // ✅ NEW: CHANGE PASSWORD HANDLERS
  const handleOpenChangePassword = useCallback(() => {
    setShowChangePassword(true);
  }, []);

  const handleCloseChangePassword = useCallback(() => {
    if (!forceChangePassword) {
      setShowChangePassword(false);
    }
  }, [forceChangePassword]);

  const handlePasswordChanged = useCallback(() => {
    setShowChangePassword(false);
    setForceChangePassword(false);
    
    const updatedUser = { ...user, must_change_password: false };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    
    showToast('✅ Password changed successfully!', 'success');
  }, [user, showToast]);

  const handleLogout = useCallback(async () => {
    const confirmed = window.confirm('🚪 Are you sure you want to logout?');
    
    if (!confirmed) return;

    try {
      setUser(null);
      setPermissions({});
      
      await axios.post(
        `${API_BASE_URL}/auth.php?action=logout`,
        { action: 'logout' },
        { withCredentials: true, headers: { 'Content-Type': 'application/json' } }
      );
      
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
      
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    }
  }, []);

  // ===== NOTIFICATIONS HANDLERS =====
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/notifications.php?limit=10`,
        getAuthConfig()
      );

      if (response.data.success) {
        setNotifications(response.data.notifications || []);
        
        const isApproverUser = user && (user.can_approve || user.is_approver || user.role === 'admin');
        
        if (isApproverUser) {
          try {
            const approvalResponse = await axios.get(
              `${API_BASE_URL}/approvals.php?type=pending`,
              getAuthConfig()
            );
            if (approvalResponse.data.success) {
              const pendingCount = approvalResponse.data.requests?.filter(r => r.status === 'PENDING').length || 0;
              setUnreadCount(pendingCount);
            } else {
              setUnreadCount(response.data.unread_count || 0);
            }
          } catch (err) {
            console.error('Error fetching pending approvals:', err);
            setUnreadCount(response.data.unread_count || 0);
          }
        } else {
          setUnreadCount(response.data.unread_count || 0);
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, [user]);

  const handleNotificationClick = useCallback(async (notification) => {
    setNotificationsOpen(false);

    if (notification.reference_type === 'view_all') {
      showToast('📋 Showing all notifications', 'info');
      return;
    }

    if (!notification.is_read) {
      try {
        await axios.post(
          `${API_BASE_URL}/notifications.php?action=mark_read`,
          { notification_id: notification.id },
          getAuthConfig()
        );

        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }

    if (notification.reference_type === 'approval_request') {
      if (permissions['approval_requests']) {
        setActiveTab('approval_requests');
        showToast('📋 Navigating to Approval Requests', 'info', 2000);
      } else {
        showToast('⚠️ You do not have access to Approval Requests', 'warning');
      }
    }
  }, [permissions, showToast]);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await axios.post(
        `${API_BASE_URL}/notifications.php?action=mark_all_read`,
        {},
        getAuthConfig()
      );

      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      showToast('✅ All notifications marked as read', 'success');
    } catch (error) {
      console.error('Error marking all as read:', error);
      showToast('❌ Failed to mark notifications as read', 'error');
    }
  }, [showToast]);

  const toggleNotifications = useCallback(() => {
    setNotificationsOpen(prev => !prev);
  }, []);

  const closeNotifications = useCallback(() => {
    setNotificationsOpen(false);
  }, []);

  // ===== DATA FETCHING =====
  const fetchUserPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (!token || !storedUser) {
        setLoading(false);
        navigate('/login', { replace: true });
        return;
      }

      const userObj = JSON.parse(storedUser);
      setUser(userObj);

      if (userObj.must_change_password) {
        setForceChangePassword(true);
        setShowChangePassword(true);
      }

      const response = await axios.get(
        `${API_BASE_URL}/dashboard_permissions.php?user_id=${userObj.id}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        const userPermissions = {};
        (response.data.permissions || []).forEach(perm => {
          userPermissions[perm.dashboard_name] = perm.can_access;
        });
        setPermissions(userPermissions);
        
        // ✅ Auto-navigate to Executive Dashboard if user only has executive access
        const hasExecutiveOnly = userPermissions['executive_dashboard'] && 
          !userPermissions['daily_inventory'] && 
          !userPermissions['repack_out'];
        
        if (hasExecutiveOnly) {
          setActiveTab('executive_dashboard');
          setActiveCategory('executive');
        }
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate('/login', { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // ===== COMPUTED VALUES =====
  const accessibleTabs = useMemo(() => {
    return allTabs.filter(tab =>
      permissions[tab.id] === true ||
      // Recall & De-stuff inherits the Operations Dashboard permission
      (tab.id === 'cs_recall_destuff' && permissions['cs_operations_dashboard'] === true)
    );
  }, [permissions]);

  const hasCategoryAccess = useCallback((categoryId) => {
    return allTabs.some(tab => 
      tab.category === categoryId && permissions[tab.id] === true
    );
  }, [permissions]);

  const getAccessibleTabsByCategory = useCallback((categoryId) => {
    return accessibleTabs.filter(tab => tab.category === categoryId);
  }, [accessibleTabs]);

  // ✅ Auto-set activeCategory once permissions load
  useEffect(() => {
    if (accessibleTabs.length > 0 && !activeCategory) {
      setActiveCategory(accessibleTabs[0].category);
    }
  }, [accessibleTabs, activeCategory]);

  const ActiveComponent = useMemo(() => {
    const activeTabData = allTabs.find(tab => tab.id === activeTab);
    return activeTabData?.component;
  }, [activeTab]);

  const componentProps = useMemo(() => ({
    user,
    showToast,
    showConfirmation,
    highlightedRequestId,
    clearHighlightedRequest: () => setHighlightedRequestId(null),
    // ✅ Pass correct tab index when navigating to Master Management
    ...(activeTab in MASTER_TAB_MAP && { defaultTab: MASTER_TAB_MAP[activeTab] }),
    // ✅ Cross-page navigation for Operations Dashboard → FCL Manifest deep-link
    onNavigateTab: (tabId, params = {}) => {
      const tab = allTabs.find(t => t.id === tabId);
      if (tab) {
        setActiveTab(tabId);
        setActiveCategory(tab.category);
        if (tabId === 'fcl_manifest' && params.fclNo) {
          setPrefilledFcl(params.fclNo);
        } else {
          setPrefilledFcl(null);
        }
      }
    },
    prefilledFcl: activeTab === 'fcl_manifest' ? prefilledFcl : null,
  }), [user, showToast, showConfirmation, highlightedRequestId, activeTab, prefilledFcl]);

  const isApprover = useMemo(() => {
    if (!user) return false;
    return user.can_approve || user.is_approver || user.role === 'admin';
  }, [user]);

  // ===== EFFECTS =====
  useEffect(() => {
    // Apply saved/default theme to <html> on mount
    document.documentElement.setAttribute('data-theme', currentTheme);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchUserPermissions();
  }, []);

  useEffect(() => {
    if (user) {
      fetchNotifications();

      notificationIntervalRef.current = setInterval(() => {
        fetchNotifications();
      }, 30000);

      return () => {
        if (notificationIntervalRef.current) {
          clearInterval(notificationIntervalRef.current);
        }
      };
    }
  }, [user, fetchNotifications]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner-modern"></div>
          <h2 className="loading-title">Loading Dashboard</h2>
          <p className="loading-text">Please wait...</p>
          <div className="loading-progress">
            <div className="loading-progress-bar"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user || accessibleTabs.length === 0) {
    return null;
  }

  return (
    <div className="dashboard-wrapper">
      {/* Toast */}
      <ToastNotification 
        toast={toast} 
        onClose={() => setToast({ show: false, message: '', type: '' })} 
      />

      {/* Modal */}
      <ConfirmModal 
        modal={confirmModal} 
        onClose={closeConfirmModal}
        onConfirm={handleModalConfirm}
      />

      {/* ✅ Change Password Modal */}
      {showChangePassword && (
        <ChangePassword 
          isForced={forceChangePassword}
          onPasswordChanged={handlePasswordChanged}
          onClose={handleCloseChangePassword}
        />
      )}

      {/* ── TOP HEADER ── */}
      <header className="header-modern">
        <div className="header-container">
          <div className="header-left">
            <button
              className="modules-back-btn"
              onClick={() => navigate('/modules')}
              title="Back to Modules"
            >
              ← Modules
            </button>
            <div className="header-logo">
              <div className="logo-text">
                <h1 className="logo-title">Warehouse System</h1>
                <p className="logo-subtitle">Choice Canning Co.</p>
              </div>
            </div>
          </div>
          <div className="header-right">
            {/* Search Bar */}
            <div className="search-bar-container">
              <div className="search-input-wrapper">
                <span className="search-icon">🔍</span>
                <input 
                  type="text" 
                  className="search-input" 
                  placeholder="Search..." 
                />
              </div>
            </div>
            <ThemePicker 
              onThemeChange={handleThemeChange} 
              showToast={showToast}
            />
            <NotificationsDropdown
              notifications={notifications}
              unreadCount={unreadCount}
              isOpen={notificationsOpen}
              onToggle={toggleNotifications}
              onNotificationClick={handleNotificationClick}
              onMarkAllRead={handleMarkAllRead}
              onClose={closeNotifications}
            />
            <UserDropdown 
              user={user}
              onChangePassword={handleOpenChangePassword}
              onLogout={handleLogout}
            />
          </div>
        </div>
      </header>

      {/* ── CATEGORY NAV BAR ── */}
      <nav className="category-nav-bar">
        {menuCategories.map(category => {
          if (!hasCategoryAccess(category.id)) return null;
          return (
            <button
              key={category.id}
              className={`category-nav-btn ${activeCategory === category.id ? 'active' : ''}`}
              style={{ '--cat-color': category.color }}
              onClick={() => handleCategoryClick(category.id)}
            >
              <span className="cat-nav-icon">{category.icon}</span>
              <span className="cat-nav-label">{category.label}</span>
              {category.id === 'admin' && isApprover && unreadCount > 0 && (
                <span className="cat-nav-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
              )}
            </button>
          );
        })}
      </nav>

      {/* ── SUB-TAB NAV BAR ── */}
      <nav className="subtab-nav-bar">
        {getAccessibleTabsByCategory(activeCategory).map(tab => (
          <button
            key={tab.id}
            className={`subtab-btn ${activeTab === tab.id ? 'active' : ''}`}
            style={{ '--tab-color': tab.color }}
            onClick={() => handleTabChange(tab.id)}
          >
            <span className="subtab-icon">{tab.icon}</span>
            <span className="subtab-label">{tab.label}</span>
            {tab.id === 'approval_requests' && isApprover && unreadCount > 0 && (
              <span className="subtab-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </button>
        ))}
      </nav>

      {/* ── MAIN CONTENT ── */}
      <main className="main-content-modern">
        <div className="content-wrapper">
          <div className="component-container">
            {ActiveComponent && <ActiveComponent {...componentProps} />}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
